'use client';
import React from 'react';
import { Popover, PopoverState, PositionTransformValue } from 'react-tiny-popover';
import styled from '@benzinga/themetron';
import { TickerHover } from './hover';
import { StockSymbol } from '@benzinga/session';

export interface Props {
  align?: 'start' | 'center' | 'end';
  positions?: ('right' | 'top' | 'bottom' | 'left')[];
  modules: React.ReactNode;
  symbol: StockSymbol;
  className?: string;
}

export const TickerPopover: React.FC<React.PropsWithChildren<Props>> = props => {
  // we are using counter simply to make sure that we dont disable popover accidentally because of the setTimeout
  const [state, setState] = React.useState({
    isDialog: false,
    isOverChildren: false,
    isOverContent: false,
  });

  const handleContentMouseEnter = React.useCallback(() => {
    setState(old => ({ ...old, isOverContent: true }));
  }, []);

  const handleContentMouseLeave = React.useCallback(() => {
    setState(old => ({ ...old, isOverContent: false }));
  }, []);

  const handleChildrenMouseEnter = React.useCallback(() => {
    setState(old => ({ ...old, isOverChildren: true }));
  }, []);

  const handleChildrenMouseLeave = React.useCallback(() => {
    setState(old => ({ ...old, isOverChildren: false }));
  }, []);

  const onDialogModeChange = React.useCallback((isDialog: boolean) => {
    setState(old => ({
      ...old,
      isDialog,
    }));
  }, []);

  const onClose = React.useCallback(() => {
    setState(old => ({ ...old, isDialog: false, isOverChildren: false, isOverContent: false }));
  }, []);

  const transform = React.useCallback<(popoverState: PopoverState) => PositionTransformValue>(popoverState => {
    const childRect = popoverState.childRect;
    const popoverRect = popoverState.popoverRect;
    const windowRect = window.document.body.getBoundingClientRect();

    const x = (() => {
      if (childRect.right + popoverRect.width > windowRect.width) {
        //if right side blocked
        if (childRect.left - popoverRect.width < 0) {
          //if left side blocked
          return windowRect.width - popoverRect.width;
        }
        return childRect.left - popoverRect.width;
      }
      return childRect.right;
    })();
    const y = (() => {
      if (childRect.top + popoverRect.height > windowRect.height) {
        //if bottom blocked
        if (childRect.top - popoverRect.height < 0) {
          //if top blocked
          return windowRect.height - popoverRect.height;
        }
        return childRect.top - popoverRect.height;
      }
      return childRect.top;
    })();

    return { left: x, top: y };
  }, []);

  return (
    <Popover
      align={props.align ?? 'start'}
      containerStyle={{ zIndex: '200' }}
      content={
        <TickerHover
          modules={props.modules}
          onClose={onClose}
          onDialogModeChange={onDialogModeChange}
          onMouseEnter={handleContentMouseEnter}
          onMouseLeave={handleContentMouseLeave}
          symbol={props.symbol}
        />
      }
      isOpen={state.isOverContent || state.isOverChildren || state.isDialog}
      positions={props.positions ?? ['right']}
      transform={transform}
      transformMode="absolute"
    >
      <InnerContainer
        className={props.className}
        onMouseEnter={handleChildrenMouseEnter}
        onMouseLeave={handleChildrenMouseLeave}
      >
        {props.children}
      </InnerContainer>
    </Popover>
  );
};

const InnerContainer = styled.span`
  cursor: pointer;
  width: 100%;
  height: 100%;
`;
